import { useState } from "react";
import { Type } from "@themes/pollinator/ui/Type";
import { Link } from "react-router-dom";
import { Settings, Video, Edit, Focus } from "lucide-react";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { DevicePermissionInitializer } from "@platform/features/video/components/DevicePermissionInitializer";
import SpeakerDevices from "@platform/features/video/components/DeviceSelection/SpeakerDevices";
import MicSelection from "@platform/features/video/components/DeviceSelection/MicrophoneDevices/MicSelection";
import MicrophoneActivityPreview from "@platform/features/video/components/DeviceSelection/MicrophoneDevices/MicrophoneActivityPreview";
import { CameraSelection } from "@platform/features/video/components/DeviceSelection/CameraDevices/CameraSelection";
import MeetingJoinDetails from "@platform/features/video/containers/MeetingJoinDetails";
import type { MeetingDetails } from "@core/features/videoMeeting";
import { PreviewVideo } from "@platform/features/video/components/DeviceSelection/CameraDevices/PreviewVideo";
import { VideoMeetingRosterItem } from "../VideoMeetingRoster/VideoMeetingRosterItem";
import useUserFullname from "@platform/hooks/useUserFullname";
import { Button } from "@themes/pollinator/ui/Button";
import { useBlurAnnouncement } from "@core/features/video";
import {
  usePlatformStore,
  useVideoMeetingAccess,
  useVideoConfig,
} from "@platform/store";
import VideoMeetingRoleAndDescriptionContainer from "../VideoMeetingRoleAndDescription/VideoMeetingRoleAndDescriptionContainer";
import { useDevices } from "@platform/features/video/providers/DevicesProvider";
import VideoMeetingPermissionsTip from "../VideoMeetingPermissions/VideoMeetingPermissionsTip";
import VideoMeetingPermissionsGuide from "../VideoMeetingPermissions/VideoMeetingPermissionsGuide";

type Props = {
  meetingDetails: MeetingDetails | null;
  meetingStartTime?: string;
  isDevicesLoading: boolean;
  onJoinMeeting: () => Promise<void>;
  isActionLoading: boolean;
};

export function VideoMeetingJoin({
  meetingDetails,
  meetingStartTime,
  isDevicesLoading,
  onJoinMeeting,
  isActionLoading,
}: Props) {
  const userFullname = useUserFullname();
  useDevices(); // Ensures DevicesProvider is active for child components
  const user = usePlatformStore((s) => s.user);
  const { roomAccess } = useVideoMeetingAccess();
  const { isBackgroundBlurEnabled, setBackgroundBlurEnabled } =
    useVideoConfig();
  const blurStatusMessage = useBlurAnnouncement(isBackgroundBlurEnabled);

  // Detect iOS Chrome once on mount and keep disclaimer visible throughout join flow
  const [isIOSChrome] = useState(() => {
    const ua = navigator.userAgent;
    return (
      /CriOS/i.test(ua) || (/iPhone|iPad|iPod/i.test(ua) && /Chrome/i.test(ua))
    );
  });

  if (!meetingDetails) {
    return null;
  }

  const roomMemberData =
    roomAccess?.room_members?.find((m) => m.user_id === user?.id) ?? null;

  const showIOSChromeDisclaimer = isIOSChrome;

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-start">
        <div className="flex flex-col flex-1 min-w-0">
          <AppPageHeader
            title={meetingDetails.title}
            description={
              meetingStartTime ? `Scheduled for ${meetingStartTime}` : undefined
            }
            titleTypeVariant="h2"
            className="gap-4"
            containerClassName="md:mb-0"
          />
          <OuterContentContainer className="md:py-0 flex-1">
            <div className="flex flex-col-reverse w-full md:max-w-lg md:flex-col py-8 md:py-0">
              <div className="flex flex-col pb-16 gap-8 md:py-8">
                <div className="flex flex-col gap-8 w-full sm:gap-16 md:gap-32">
                  <div className="flex flex-col-reverse gap-8 w-full md:max-w-lg md:gap-12 md:flex-col">
                    {showIOSChromeDisclaimer && (
                      <div className="flex flex-col gap-2">
                        <Type as="h2" variant="h4" className="text-error-700">
                          iOS Chrome Detected
                        </Type>
                        <div className="p-4 border border-error-700 rounded-md flex flex-col gap-2">
                          <Type variant="caption" className="text-error-700">
                            You may need to turn on microphone access in
                            settings via Settings → Privacy & Security →
                            Microphone Settings (enable Chrome). Alternatively,
                            you can use iOS Safari for better compatibility.
                          </Type>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col gap-3">
                      <Type
                        as="h2"
                        variant="h4"
                        className="flex items-center gap-2"
                      >
                        How You'll Appear to Others
                      </Type>
                      <div className="flex flex-col gap-2 items-start">
                        <div className="border border-brand-800 rounded-md p-4 shadow-md w-full">
                          <VideoMeetingRosterItem
                            muted={false}
                            videoEnabled={true}
                            sharingContent={false}
                            attendeeName={userFullname}
                            roomMemberData={roomMemberData}
                          />
                        </div>
                        <Button
                          as={Link}
                          variant="link"
                          iconComponent={Edit}
                          to={`/program/${meetingDetails.program}/profile`}
                        >
                          Edit Profile
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-8 border border-brand-800 rounded-md p-4 shadow-md bg-brand-100">
                        <VideoMeetingRoleAndDescriptionContainer
                          meetingDetails={meetingDetails}
                        />
                        <MeetingJoinDetails
                          isDevicesLoading={isDevicesLoading}
                          isActionLoading={isActionLoading}
                          onJoinMeeting={onJoinMeeting}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-0 sm:gap-2 items-start sm:items-center lg:gap-2 lg:items-center">
                        <Type
                          as="h2"
                          variant="h6"
                          className="flex items-center gap-2 text-error-700"
                        >
                          Having issues with your camera or microphone?
                        </Type>
                        <VideoMeetingPermissionsTip />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </OuterContentContainer>
        </div>
        <div className="w-full md:w-[384px] md:shrink-0 md:self-stretch md:border-l md:border-brand-800 md:bg-brand-50">
          <div className="flex flex-col gap-8 px-4 py-8 pb-8 md:sticky md:top-[71px] md:max-h-[calc(100vh-71px)] md:py-4 md:px-4 md:pb-8 md:gap-4 md:overflow-y-auto">
            <div className="flex flex-col gap-2">
              <Type as="h2" variant="h4" className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Video Preview
              </Type>
              <PreviewVideo />
            </div>

            <div className="flex flex-col gap-4">
              <Type as="h2" variant="h4" className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Device Settings
              </Type>
              <Type variant="caption" className="text-brand-700">
                You can join the meeting even if you don't have a camera
                connected.
              </Type>
              <DevicePermissionInitializer />
              <CameraSelection />
              <Button
                variant={isBackgroundBlurEnabled ? "primary" : "secondary"}
                iconComponent={Focus}
                aria-pressed={isBackgroundBlurEnabled}
                aria-label={
                  isBackgroundBlurEnabled
                    ? "Background blur on, press to turn off"
                    : "Background blur off, press to turn on"
                }
                onClick={() =>
                  setBackgroundBlurEnabled(!isBackgroundBlurEnabled)
                }
              >
                {isBackgroundBlurEnabled ? "Blur On" : "Blur Off"}
              </Button>
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
              >
                {blurStatusMessage}
              </div>
              <MicSelection />
              <MicrophoneActivityPreview />
              <SpeakerDevices />
            </div>
            <div className="flex flex-col gap-2 md:hidden">
              <Type
                as="h3"
                variant="h6"
                className="flex items-center gap-2 text-brand-700"
              >
                Ready to Join?
              </Type>
              <MeetingJoinDetails
                isDevicesLoading={isDevicesLoading}
                isActionLoading={isActionLoading}
                onJoinMeeting={onJoinMeeting}
              />
            </div>
          </div>
        </div>
      </div>
      <VideoMeetingPermissionsGuide />
    </>
  );
}
